import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import CloseIcon from '@mui/icons-material/Close';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import { useRealtime } from '../realtime/RealtimeContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';
import ViewModeToggle from '../components/table/ViewModeToggle';

const debounceDelay = 300;
const defaultRowsPerPage = 25;

const TABLE_COLUMNS = [
  { key: 'mockup', label: 'Mockup', minWidth: 240, maxWidth: 520, defaultWidth: 320 },
  { key: 'catalog', label: 'Catalog', minWidth: 160, maxWidth: 340, defaultWidth: 200 },
  { key: 'status', label: 'Status', minWidth: 110, maxWidth: 220, defaultWidth: 130 },
  { key: 'format', label: 'Format', minWidth: 90, maxWidth: 180, defaultWidth: 110 },
  { key: 'size', label: 'Size', minWidth: 110, maxWidth: 220, defaultWidth: 130 },
  { key: 'updated', label: 'Updated', minWidth: 170, maxWidth: 340, defaultWidth: 210 },
];

const DynamicMockupsPage = () => {
  const { fetchWithAuth } = useAuth();
  const { latestJobUpdate } = useRealtime();
  const [accounts, setAccounts] = React.useState([]);
  const [accountsLoading, setAccountsLoading] = React.useState(false);
  const [selectedAccountId, setSelectedAccountId] = React.useState('');
  const [catalogs, setCatalogs] = React.useState([]);
  const [catalogsLoading, setCatalogsLoading] = React.useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [actionMessage, setActionMessage] = React.useState(null);
  const [syncing, setSyncing] = React.useState(false);
  const [viewMode, setViewMode] = React.useState('tiles');
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailMockup, setDetailMockup] = React.useState(null);
  const refreshTimerRef = React.useRef(null);

  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.dynamic-mockups',
  });

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), debounceDelay);
    return () => window.clearTimeout(handle);
  }, [search]);

  const loadAccounts = React.useCallback(async () => {
    setAccountsLoading(true);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/accounts`);
      if (!resp.ok) {
        throw new Error('Failed to load accounts');
      }
      const payload = await resp.json();
      const nextAccounts = Array.isArray(payload.items) ? payload.items : [];
      setAccounts(nextAccounts);
      setSelectedAccountId((current) => {
        if (current && nextAccounts.some((item) => item.id === current)) {
          return current;
        }
        return nextAccounts[0]?.id || '';
      });
    } catch (err) {
      console.error(err);
    } finally {
      setAccountsLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const loadCatalogs = React.useCallback(async () => {
    if (!selectedAccountId) {
      setCatalogs([]);
      setSelectedCatalogId('');
      return;
    }
    setCatalogsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('account_id', selectedAccountId);
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/catalogs?${params.toString()}`);
      if (!resp.ok) {
        throw new Error('Failed to load catalogs');
      }
      const payload = await resp.json();
      const nextCatalogs = Array.isArray(payload.items) ? payload.items : [];
      setCatalogs(nextCatalogs);
      setSelectedCatalogId((current) =>
        current && nextCatalogs.some((item) => item.id === current) ? current : '',
      );
    } catch (err) {
      console.error(err);
      setCatalogs([]);
      setSelectedCatalogId('');
    } finally {
      setCatalogsLoading(false);
    }
  }, [fetchWithAuth, selectedAccountId]);

  React.useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  const loadMockups = React.useCallback(async () => {
    if (!selectedAccountId) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('account_id', selectedAccountId);
      params.set('limit', rowsPerPage.toString());
      params.set('offset', (page * rowsPerPage).toString());
      if (debouncedSearch) {
        params.set('q', debouncedSearch);
      }
      if (selectedCatalogId) {
        params.set('catalog_id', selectedCatalogId);
      }
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/mockups?${params.toString()}`);
      if (!resp.ok) {
        throw new Error('Failed to load mockups');
      }
      const payload = await resp.json();
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
    } catch (err) {
      setError(err.message || 'Failed to load mockups');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, selectedAccountId, rowsPerPage, page, debouncedSearch, selectedCatalogId]);

  React.useEffect(() => {
    loadMockups();
  }, [loadMockups]);

  React.useEffect(() => {
    if (!latestJobUpdate || latestJobUpdate.type !== 'job.updated') {
      return;
    }
    const job = latestJobUpdate.job;
    if (!job || Number(job.status) !== 2 || job.name !== 'SyncDynamicMockups') {
      return;
    }
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setTimeout(() => {
      loadCatalogs();
      loadMockups();
    }, 500);

    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
      }
    };
  }, [latestJobUpdate, loadCatalogs, loadMockups]);

  React.useEffect(() => {
    setPage(0);
  }, [selectedAccountId, selectedCatalogId, rowsPerPage]);

  const handleSync = async () => {
    if (!selectedAccountId) {
      return;
    }
    setSyncing(true);
    setError(null);
    setActionMessage(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/mockups/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: selectedAccountId }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to queue sync');
      }
      setActionMessage({ type: 'info', text: 'Mockups sync queued. Progress is available in Team Jobs.' });
    } catch (err) {
      setError(err.message || 'Failed to queue sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDetail = (mockup) => {
    if (!mockup) {
      return;
    }
    setDetailMockup(mockup);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetailMockup(null);
  };

  const emptyStateLabel = selectedAccountId
    ? 'No mockups found for this account.'
    : 'Select an account to view mockups.';

  return (
    <Stack spacing={2} sx={{ height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Account"
          value={selectedAccountId}
          disabled={accountsLoading || accounts.length === 0}
          onChange={(event) => setSelectedAccountId(event.target.value)}
          sx={{ minWidth: 240 }}
        >
          {accounts.map((account) => (
            <MenuItem key={account.id} value={account.id}>
              {account.name || account.id}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Catalog"
          value={selectedCatalogId}
          onChange={(event) => setSelectedCatalogId(event.target.value)}
          disabled={!selectedAccountId || catalogsLoading || catalogs.length === 0}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All catalogs</MenuItem>
          {catalogs.map((catalog) => (
            <MenuItem key={catalog.id} value={catalog.id}>
              {catalog.name || catalog.external_id}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search mockups"
          placeholder="Name or ID"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ flexGrow: 1, minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<SyncIcon />}
          onClick={handleSync}
          disabled={!selectedAccountId || syncing}
        >
          {syncing ? 'Syncing…' : 'Sync Mockups'}
        </Button>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {actionMessage ? <Alert severity={actionMessage.type}>{actionMessage.text}</Alert> : null}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
      </Box>

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {viewMode === 'rows' ? (
          <TableContainer sx={{ flex: 1 }}>
            <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
              <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
              <TableHead>
                <TableRow>
                  {TABLE_COLUMNS.map((column) => (
                    <ResizableHeaderCell
                      key={column.key}
                      column={column}
                      getWidth={getWidth}
                      onResizeStart={startColumnResize}
                    />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading mockups..." />
                ) : items.length === 0 ? (
                  <TableEmptyRow colSpan={TABLE_COLUMNS.length} label={emptyStateLabel} />
                ) : (
                  items.map((item) => (
                    <TableRow
                      key={item.id || item.external_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleOpenDetail(item)}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          {selectPreviewImage(item) ? (
                            <Avatar
                              variant="rounded"
                              src={selectPreviewImage(item)}
                              alt={item.name || item.external_id}
                              sx={{ width: 56, height: 56 }}
                            />
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 56, height: 56 }}>
                              <DashboardCustomizeIcon />
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="subtitle2">{item.name || item.external_id}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              ID: {item.external_id || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{item.catalog?.name || '—'}</TableCell>
                      <TableCell>
                        <Chip label={item.status || '—'} size="small" color={item.status === 'ready' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>{item.format || '—'}</TableCell>
                      <TableCell>{formatDimensions(item.width, item.height)}</TableCell>
                      <TableCell>{formatTimestamp(item.updated_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
            {loading ? (
              <Typography variant="body2" color="text.secondary">Loading mockups...</Typography>
            ) : items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{emptyStateLabel}</Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: 2,
                }}
              >
                {items.map((item) => (
                  <Paper
                    key={item.id || item.external_id}
                    variant="outlined"
                    onClick={() => handleOpenDetail(item)}
                    sx={{
                      cursor: 'pointer',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ position: 'relative', bgcolor: 'grey.100' }}>
                      {selectPreviewImage(item) ? (
                        <Box
                          component="img"
                          src={selectPreviewImage(item)}
                          alt={item.name || item.external_id}
                          sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.secondary',
                          }}
                        >
                          <DashboardCustomizeIcon />
                        </Box>
                      )}
                      <Chip
                        label={item.status || '—'}
                        size="small"
                        color={item.status === 'ready' ? 'success' : 'default'}
                        sx={{ position: 'absolute', top: 10, right: 10 }}
                      />
                    </Box>
                    <Stack spacing={0.5} sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2" noWrap title={item.name || item.external_id}>
                        {item.name || item.external_id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Catalog: {item.catalog?.name || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDimensions(item.width, item.height)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Updated: {formatTimestamp(item.updated_at)}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      <DynamicMockupDetailDialog open={detailOpen} mockup={detailMockup} onClose={handleCloseDetail} />
    </Stack>
  );
};

const DynamicMockupDetailDialog = ({ open, mockup, onClose }) => {
  const rawJSON = React.useMemo(() => {
    if (!mockup?.raw_data) {
      return '{}';
    }
    try {
      return JSON.stringify(mockup.raw_data, null, 2);
    } catch {
      return '{}';
    }
  }, [mockup]);

  const previewURL = selectPreviewImage(mockup);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Box>
          <Typography variant="h6">{mockup?.name || 'Mockup Details'}</Typography>
          <Typography variant="body2" color="text.secondary">
            ID: {mockup?.external_id || '—'}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
          <Box sx={{ flex: { xs: 'auto', md: 1.5 }, width: '100%' }}>
            {previewURL ? (
              <Box
                component="img"
                src={previewURL}
                alt={mockup?.name || mockup?.external_id}
                sx={{ width: '100%', borderRadius: 2 }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  minHeight: 260,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                No preview available
              </Box>
            )}
          </Box>
          <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
          <Stack spacing={1.5} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={mockup?.status || '—'} size="small" color={mockup?.status === 'ready' ? 'success' : 'default'} />
              <Chip label={mockup?.format || '—'} size="small" variant="outlined" />
            </Stack>
            <Stack spacing={1}>
              <InfoRow label="Catalog" value={mockup?.catalog?.name || '—'} />
              <InfoRow label="Size" value={formatDimensions(mockup?.width, mockup?.height)} />
              <InfoRow label="Source URL" value={mockup?.source_url || '—'} />
              <InfoRow label="Updated" value={formatTimestamp(mockup?.updated_at)} />
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle2">Raw Data</Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  fontSize: 12,
                  lineHeight: 1.5,
                  overflow: 'auto',
                  maxHeight: 320,
                }}
              >
                {rawJSON}
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const InfoRow = ({ label, value }) => (
  <Stack direction="row" spacing={1} justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', maxWidth: '70%' }}>
      {value ?? '—'}
    </Typography>
  </Stack>
);

function formatDimensions(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (!w || !h) {
    return '—';
  }
  return `${w} × ${h}`;
}

function formatTimestamp(value) {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleString();
}

function selectPreviewImage(item) {
  if (!item) {
    return '';
  }
  const candidates = [item.preview_url, item.thumbnail_url, item.render_url, item.source_url];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate.trim();
    }
  }
  return '';
}

export default DynamicMockupsPage;
